/**
 * @file TodoFormSheet.tsx
 * @brief Add or edit a to-do the meeting did not produce.
 *
 * @details
 * The web twin of `FRONTEND/.../MeetingDetails/components/TodoFormSheet.tsx`,
 * and the same decision: add and edit are one form. They take the same three
 * fields, so a second near-identical panel would be a second place for the
 * assignee rules and the date handling to drift apart.
 *
 * It began as a card inline in the list, which worked for adding — it always
 * appeared at the top — and had nowhere sensible to put an edit: a form for a
 * row near the bottom would open far from the row it belonged to. The right
 * rail is where this panel does create/edit everywhere else (`FormSheet`), so
 * that is where it went (05-08-2026).
 *
 * Two rules the item itself must respect, mirrored from the API:
 *
 *  1. **It never pretends to be evidence.** Stored with `source: "manual"` and
 *     no anchor, and the row that renders it says so — the verified /
 *     unverified distinction the anchors work built collapses the moment
 *     something with no provenance looks identical to something with it.
 *  2. **The assignee may be anyone in the company**, participant or not — the
 *     opposite of the rule for extracted items, and deliberately so: there the
 *     model is guessing, here a person is taking responsibility.
 *
 * **Reassign mode (28-09-2026).** Opened on a to-do from the recording, the
 * picker also offers Team and the people who spoke in this meeting. Mirrors
 * the mobile sheet.
 *
 * **Edit to-do (B48, DES 02-11-2026).** The same sheet is now titled "Edit
 * to-do" for an AI to-do too, and its wording (a mis-heard word) and due date
 * can change. The server keeps the first AI wording (`originalTask`) and the
 * evidence quote, so the link to the recording still checks what was said;
 * the row then says "Edited" with the original under it. The assignee keys
 * go only when the person changed (a body without them keeps the person).
 * The picker offers "Add someone: <typed name>" (Name + Email, both
 * required) and the people already given a to-do that way on this meeting
 * (`external_assignees`).
 *
 * @dependencies
 * - company-meetings/api: useAddMeetingTodo, useUpdateMeetingTodo
 * - components/ui/FormSheet, AssignablePersonPicker, DatePicker
 *
 * @references
 * - MeetingDetailView.tsx — opens it from the header and from a row's pencil
 * - BACKEND POST /boardroom/sessions/:id/todos, PUT …/todos/:todoId
 *
 * @author DES
 * @date 05-08-2026
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { cn } from "@/shared/theme/cn";
import { toast } from "@/shared/hooks/useToast";
import {
  useAddMeetingTodo,
  useAssignablePeople,
  useUpdateMeetingTodo,
} from "../api";
import type { MeetingTodo } from "../schemas";
import {
  AssignablePersonPicker,
  type AssignableExtraItem,
} from "./AssignablePersonPicker";

/** Reassign mode: the reserved "Team" assignee (BACKEND todoAssignee.mjs). */
const TEAM_ID = "team";
const TEAM_CODE = "__team__";
const SPEAKER_PREFIX = "spk:";
/** A person added with "Add someone", or one from `external_assignees`. */
const NEW_PREFIX = "new:";
/** The person the to-do is for now when no list has them (a guest with no
 *  speaker track and no email): shown by name, never sent back. */
const CURRENT_CODE = "__current__";

/** Someone given a to-do on this meeting with "Add someone" (B48). */
export type TodoSheetExternalAssignee = {
  name?: string | null;
  email?: string | null;
  userId?: string | null;
  profileImageUrl?: string | null;
};

/** A speaker of this meeting, as the reassign picker needs it. */
export type TodoSheetSpeaker = {
  speakerId: string;
  name: string;
  email?: string | null;
  avatarUrl?: string | null;
};

/**
 * `dueDate` is stored as the display string the synthesis writes
 * ("August 06, 2026"). The picker speaks ISO, so an existing value is parsed
 * back — anything unparseable simply opens empty rather than showing a date
 * the item does not have.
 */
function toIsoDate(due?: string | null): string | undefined {
  const raw = String(due ?? "").trim();
  if (!raw) return undefined;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

export function TodoFormSheet({
  open,
  onOpenChange,
  meetingId,
  editing: editingProp,
  speakers,
  externalAssignees,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meetingId: string;
  /** An existing to-do: a MANUAL one opens the full edit, one from the
   *  recording opens reassign mode. Absent means a new one. */
  editing?: MeetingTodo | null;
  /** This meeting's speakers, offered in reassign mode. */
  speakers?: TodoSheetSpeaker[];
  /** People given a to-do on this meeting with "Add someone". */
  externalAssignees?: TodoSheetExternalAssignee[] | null;
}) {
  // Hold the to-do the sheet was opened with while it animates out: the
  // caller clears `editing` as it closes, which flashed the "New to-do" form
  // for the length of the exit (DES 05-10-2026).
  const heldEditing = useRef<MeetingTodo | null>(editingProp ?? null);
  if (open) heldEditing.current = editingProp ?? null;
  const editing = open ? (editingProp ?? null) : heldEditing.current;
  const addTodo = useAddMeetingTodo();
  const updateTodo = useUpdateMeetingTodo();
  const [task, setTask] = useState("");
  const [assigneeUserId, setAssigneeUserId] = useState<string | null>(null);
  const [dueDateIso, setDueDateIso] = useState<string | undefined>(undefined);
  // People added with "Add someone" while the sheet is open, by email.
  const [added, setAdded] = useState<{ name: string; email: string }[]>([]);
  // What the sheet opened with, to send only what changed (B48).
  const seeded = useRef<{ assignee: string | null; due: string | undefined }>({
    assignee: null,
    due: undefined,
  });

  const editingId = editing?.id ?? null;
  const pending = addTodo.isPending || updateTodo.isPending;
  const reassignOnly = !!editing && editing.source !== "manual";
  const people = useAssignablePeople(open);

  // Team first, then people who spoke but are not in the company list, then
  // people given a to-do here with "Add someone" (and any just added).
  const extraItems = useMemo<AssignableExtraItem[] | undefined>(() => {
    const emails = new Set(
      (people.data ?? [])
        .map((p) => String(p.email ?? "").toLowerCase())
        .filter(Boolean),
    );
    const outside = new Map<string, AssignableExtraItem>();
    for (const x of [...(externalAssignees ?? []), ...added]) {
      const email = String(x.email ?? "").trim().toLowerCase();
      const name = String(x.name ?? "").trim();
      if (!email || !name || emails.has(email) || outside.has(email)) continue;
      outside.set(email, {
        id: `${NEW_PREFIX}${email}`,
        label: name,
        // Never an email in a name slot; a second line says who they are.
        subtitle: "Added to a to-do",
        avatarUrl: (x as TodoSheetExternalAssignee).profileImageUrl ?? null,
      });
    }
    const spoke = reassignOnly
      ? (speakers ?? [])
          .filter((sp) => {
            const e = String(sp.email ?? "").toLowerCase();
            return !!sp.name.trim() && !(e && (emails.has(e) || outside.has(e)));
          })
          .map((sp) => ({
            id: `${SPEAKER_PREFIX}${sp.speakerId}`,
            label: sp.name,
            subtitle: "In this meeting",
            avatarUrl: sp.avatarUrl ?? null,
          }))
      : [];
    const currentName = String((editing as { assigneeName?: string | null } | null)?.assigneeName ?? "").trim();
    const current =
      reassignOnly && currentName && seeded.current.assignee === CURRENT_CODE
        ? [{ id: CURRENT_CODE, label: currentName, subtitle: "Guest" }]
        : [];
    const rows = [
      ...(reassignOnly
        ? [{ id: TEAM_CODE, label: "Team", subtitle: "The whole group", isTeam: true }]
        : []),
      ...current,
      ...spoke,
      ...outside.values(),
    ];
    return rows.length ? rows : undefined;
    // `assigneeUserId` re-runs it after the seed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reassignOnly, speakers, people.data, externalAssignees, added, editing, assigneeUserId]);

  // Seeded on OPEN, keyed by which item is open, so a re-render never
  // overwrites what is being typed. An AI to-do has no `assigneeUserId`, so
  // its current person is found by email (then Team, then its speaker).
  useEffect(() => {
    if (!open) return;
    setTask(editing?.task ?? "");
    const due = toIsoDate(editing?.dueDate);
    setDueDateIso(due);
    setAdded([]);
    const seed = (assignee: string | null) => {
      seeded.current = { assignee, due };
      setAssigneeUserId(assignee);
    };
    if (editing?.assigneeSpeakerId === TEAM_ID) return seed(TEAM_CODE);
    const email = String(editing?.assigneeEmail ?? "").toLowerCase().trim();
    const byEmail = email
      ? (people.data ?? []).find((p) => String(p.email ?? "").toLowerCase() === email)
      : undefined;
    const member = (people.data ?? []).find((p) => p.id === editing?.assigneeUserId) ?? byEmail;
    if (member) return seed(member.id);
    const outside = email
      ? (externalAssignees ?? []).find((x) => String(x.email ?? "").toLowerCase() === email)
      : undefined;
    if (outside) return seed(`${NEW_PREFIX}${email}`);
    if (editing?.assigneeUserId) return seed(editing.assigneeUserId);
    const spk = (speakers ?? []).find((sp) => sp.speakerId === editing?.assigneeSpeakerId);
    const named = String((editing as { assigneeName?: string | null } | null)?.assigneeName ?? "").trim();
    seed(
      reassignOnly && spk
        ? `${SPEAKER_PREFIX}${spk.speakerId}`
        : reassignOnly && named
          ? CURRENT_CODE
          : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId, people.data]);

  /** The picked person as the API's assignee keys (B48 `newAssignee`). */
  const assigneeBody = (code: string | null) => {
    if (code?.startsWith(NEW_PREFIX)) {
      const email = code.slice(NEW_PREFIX.length);
      const p =
        added.find((x) => x.email.toLowerCase() === email) ??
        (externalAssignees ?? []).find((x) => String(x.email ?? "").toLowerCase() === email);
      return { newAssignee: { name: String(p?.name ?? "").trim(), email: String(p?.email ?? email) } };
    }
    if (reassignOnly) {
      return {
        assigneeSpeakerId:
          code === TEAM_CODE
            ? TEAM_ID
            : code?.startsWith(SPEAKER_PREFIX)
              ? code.slice(SPEAKER_PREFIX.length)
              : null,
        assigneeUserId:
          code && code !== TEAM_CODE && !code.startsWith(SPEAKER_PREFIX) ? code : null,
      };
    }
    return { assigneeUserId: code };
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = task.trim();
    if (!text || pending) return;

    try {
      if (reassignOnly && editingId) {
        // B48: only what changed. Without assignee keys the person is kept;
        // an empty body would mean Unassigned, so one is never sent.
        const body: Parameters<typeof updateTodo.mutateAsync>[0] = { meetingId, todoId: editingId };
        if (text !== (editing?.task ?? "").trim()) body.task = text;
        if (dueDateIso !== seeded.current.due) body.dueDate = dueDateIso ?? null;
        if (assigneeUserId !== seeded.current.assignee) Object.assign(body, assigneeBody(assigneeUserId));
        if (Object.keys(body).length > 2) await updateTodo.mutateAsync(body);
        onOpenChange(false);
        return;
      }

      // Sent as a plain calendar date; the API normalizes it to the same
      // "Month DD, YYYY" the synthesis writes. Formatting it here gave manual
      // to-dos a second date style in the same list.
      const payload = {
        meetingId,
        task: text,
        ...assigneeBody(assigneeUserId),
        dueDate: dueDateIso ?? null,
      };
      if (editingId) {
        await updateTodo.mutateAsync({ ...payload, todoId: editingId });
      } else {
        await addTodo.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Could not save this to-do",
        description: err instanceof Error ? err.message : "Please try again.",
      });
    }
  };

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* DES 01-10-2026: form standard, Cmd+K palette frame at its full
          width like the other forms ("iski width poori karo like others");
          it was capped at max-w-3xl. */}
      <FormSheetContent side="palette">
        <form
          onSubmit={submit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          noValidate
        >
          <FormSheetFrame>
            <FormSheetHeader>
              <FormSheetTitle>
                {editingId ? "Edit to-do" : "New to-do"}
              </FormSheetTitle>
              {/* B48: no "From the recording" line on an AI to-do (DES 02-11-2026). */}
              {reassignOnly ? null : (
                <FormSheetDescription>
                  Added by hand: it carries no quote and no moment in the recording, and the list says so.
                </FormSheetDescription>
              )}
            </FormSheetHeader>

            <FormSheetBody>
              <FormSheetSection
                title="Task"
                subtitle="What needs doing, in the words the team will read."
              >
                <FormSheetFields>
                  <FormSheetField span={2}>
                    <FormFieldGroup label="Description" htmlFor="todo-task" required>
                      <textarea
                        id="todo-task"
                        autoFocus={!reassignOnly}
                        value={task}
                        onChange={(e) => setTask(e.target.value)}
                        placeholder="Describe the follow-up…"
                        maxLength={500}
                        rows={3}
                        className={cn(
                          "flex min-h-[5.5rem] w-full resize-y rounded-[1.2rem] border border-input/90 bg-field px-5 py-3 text-base font-medium text-foreground ring-offset-background",
                          "placeholder:font-normal placeholder:text-muted-foreground",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                          "disabled:cursor-not-allowed disabled:opacity-50",
                        )}
                      />
                      <p className="mt-1 text-[11px] tabular-nums text-muted-foreground">
                        {task.trim().length}/500
                      </p>
                      {/* What was said stays with the evidence quote. */}
                      {reassignOnly && editing?.originalTask && editing.originalTask.trim() !== task.trim() ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Said: “{editing.originalTask.trim()}”
                        </p>
                      ) : null}
                    </FormFieldGroup>
                  </FormSheetField>
                </FormSheetFields>
              </FormSheetSection>

              <FormSheetSection
                title="Ownership"
                subtitle="Anyone in the company, or someone new by name and email. They need not have been in the meeting."
              >
                <FormSheetFields>
                  <FormSheetField>
                    {/* A hint here too, so its hairline lines up with Due
                        date's (DES 01-10-2026: form standard). */}
                    <FormFieldGroup
                      label="Assign to"
                      hint={reassignOnly ? "Who it is for now." : "Optional."}
                    >
                      <AssignablePersonPicker
                        value={assigneeUserId}
                        onChange={setAssigneeUserId}
                        disabled={pending}
                        extraItems={extraItems}
                        onAddSomeone={(p) => {
                          const email = p.email.trim().toLowerCase();
                          const member = (people.data ?? []).find(
                            (x) => String(x.email ?? "").toLowerCase() === email,
                          );
                          if (member) {
                            setAssigneeUserId(member.id);
                            return;
                          }
                          setAdded((cur) => [
                            ...cur.filter((x) => x.email.toLowerCase() !== email),
                            { name: p.name, email: p.email.trim() },
                          ]);
                          setAssigneeUserId(`${NEW_PREFIX}${email}`);
                        }}
                        subtitle={
                          reassignOnly
                            ? "Team, someone from this meeting, anyone in your company, or type a name to add someone."
                            : "Anyone in your company, or type a name to add someone."
                        }
                      />
                    </FormFieldGroup>
                  </FormSheetField>
                  <FormSheetField>
                    {/* Optional, and no invented default: a deadline nobody
                        agreed to is worse than none. */}
                    <FormFieldGroup
                      label="Due date"
                      hint="Optional."
                    >
                      <DatePicker
                        value={dueDateIso}
                        onChange={setDueDateIso}
                        placeholder="No due date"
                        title="Due date"
                        description="Optional. When should this be done?"
                        size="md"
                      />
                    </FormFieldGroup>
                  </FormSheetField>
                </FormSheetFields>
              </FormSheetSection>
            </FormSheetBody>

            <FormSheetFooter>
              <FormSheetFooterCell>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={pending}
                  className={formSheetActionClass}
                >
                  Cancel
                </Button>
              </FormSheetFooterCell>
              <FormSheetFooterCell>
                <Button
                  type="submit"
                  disabled={!task.trim() || pending}
                  className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
                >
                  {pending ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : editingId ? (
                    "Save changes"
                  ) : (
                    "Add to-do"
                  )}
                </Button>
              </FormSheetFooterCell>
            </FormSheetFooter>
          </FormSheetFrame>
        </form>
      </FormSheetContent>
    </FormSheet>
  );
}

export default TodoFormSheet;
